
import { useState } from "react";
import { FormStep, FormSection } from "@/models";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Pencil, Trash, PlusCircle, GripVertical } from "lucide-react";
import { formSectionApi, formStepApi } from "@/services/api";
import { useToast } from "@/components/ui/use-toast";
import { DragDropContext, Droppable, DropResult } from "react-beautiful-dnd";
import FormSectionList from "./FormSectionList";
import AddSectionDialog from "./dialogs/AddSectionDialog";
import EditSectionDialog from "./dialogs/EditSectionDialog";
import EditStepDialog from "./dialogs/EditStepDialog";

interface StepItemProps {
  step: FormStep;
  onSectionUpdated: () => Promise<void>;
  preventFormSubmission?: boolean;
  dragHandleProps?: any;
}

const StepItem = ({ 
  step, 
  onSectionUpdated, 
  preventFormSubmission = false,
  dragHandleProps 
}: StepItemProps) => {
  const { toast } = useToast();
  const [isAddingSectionDialog, setIsAddingSectionDialog] = useState(false);
  const [isEditingStep, setIsEditingStep] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingSection, setEditingSection] = useState<FormSection | null>(null);
  const sections = step.form_sections || [];
  const sortedSections = sections.slice().sort((a, b) => a.section_order - b.section_order);

  const handleDragEnd = async (result: DropResult) => {
    const { destination, source } = result;
    
    if (!destination) return;
    if (destination.index === source.index) return;
    
    try {
      setIsSubmitting(true);

      const reorderedSections = [...sections];
      const [movedSection] = reorderedSections.splice(source.index, 1);
      reorderedSections.splice(destination.index, 0, movedSection);
      
      const updatePromises = reorderedSections.map((section, index) => 
        formSectionApi.update(section.id, { section_order: index })
      );
      
      await Promise.all(updatePromises);
      
      toast({
        title: "Sections reordered",
        description: "The sections have been reordered successfully",
      });
      
      await onSectionUpdated();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error reordering sections",
        description: error.message || "An error occurred while reordering the sections",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddSection = async (data: { 
    section_title: string, 
    section_type: 'Default' | 'Applicants',
    multiple_elements: boolean
  }) => {
    try {
      console.log("Adding section to step:", step.id, data);
      setIsSubmitting(true);
      
      await formSectionApi.create({
        form_step_id: step.id,
        section_title: data.section_title,
        section_type: data.section_type,
        section_order: sections.length,
        multiple_elements: data.multiple_elements
      });
      
      toast({
        title: "Section added",
        description: "The section has been added successfully",
      });
      
      await onSectionUpdated();
      setIsAddingSectionDialog(false);
    } catch (error: any) {
      console.error("Error adding section:", error);
      toast({
        variant: "destructive",
        title: "Error adding section",
        description: error.message || "An error occurred while adding the section",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSection = async (sectionId: number) => {
    if (!window.confirm("Are you sure you want to delete this section? This will delete all fields in this section.")) {
      return;
    }
    
    try {
      console.log("Deleting section:", sectionId);
      setIsSubmitting(true);
      
      await formSectionApi.delete(sectionId);
      
      toast({
        title: "Section deleted",
        description: "The section has been deleted successfully",
      });
      
      await onSectionUpdated();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error deleting section",
        description: error.message || "An error occurred while deleting the section",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditStep = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log("Edit step clicked:", step.id);
    setIsEditingStep(true);
  };

  const handleUpdateStep = async (updatedStep: FormStep) => {
    try {
      console.log("Updating step:", updatedStep);
      setIsSubmitting(true);
      
      await formStepApi.update(updatedStep.id, updatedStep);
      
      toast({
        title: "Step updated",
        description: "The step has been updated successfully",
      });
      
      await onSectionUpdated();
      setIsEditingStep(false);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error updating step",
        description: error.message || "An error occurred while updating the step",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteStep = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!window.confirm("Are you sure you want to delete this step? This will delete all sections and fields in this step.")) {
      return;
    }
    
    try {
      console.log("Deleting step:", step.id);
      setIsSubmitting(true);
      
      await formStepApi.delete(step.id);
      
      toast({
        title: "Step deleted",
        description: "The step has been deleted successfully",
      });
      
      await onSectionUpdated();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error deleting step",
        description: error.message || "An error occurred while deleting the step",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSection = (section: FormSection) => {
    console.log("Editing section:", section.id);
    setEditingSection(section);
  };

  const handleOpenAddSectionDialog = (e: React.MouseEvent) => {
    console.log("Opening add section dialog for step:", step.id);
    e.preventDefault();
    e.stopPropagation();
    setIsAddingSectionDialog(true);
  };

  return (
    <Card className="mb-8">
      <CardHeader className="border-b p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {/* Enhanced drag handle with proper props */}
            <div 
              {...dragHandleProps} 
              className="cursor-grab active:cursor-grabbing"
              aria-label="Drag to reorder step"
            >
              <GripVertical className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <CardTitle className="text-xl">{step.step_title || `Step ${step.step_order + 1}`}</CardTitle>
                {step.next_step_label && (
                  <CardDescription className="mt-0">
                    Next: {step.next_step_label}
                  </CardDescription>
                )}
              </div>
            </div>
          </div>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={handleEditStep}
              disabled={isSubmitting}
            >
              <Pencil className="h-4 w-4 mr-2" />
              Edit Step
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive"
              type="button"
              onClick={handleDeleteStep}
              disabled={isSubmitting}
            >
              <Trash className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="space-y-6">
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId={`sections-step-${step.id}`} type={`section-group-${step.id}`}>
              {(provided) => (
                <FormSectionList 
                  provided={provided}
                  sections={sortedSections}
                  step={step}
                  onSectionUpdated={onSectionUpdated}
                  onDeleteSection={handleDeleteSection}
                  isParentSubmitting={isSubmitting}
                  onEditSection={handleEditSection}
                  onStepUpdated={onSectionUpdated}
                />
              )}
            </Droppable>
          </DragDropContext>
          
          <Button
            variant="outline"
            size="sm"
            type="button"
            onClick={handleOpenAddSectionDialog}
            className="w-full"
            disabled={isSubmitting}
            data-test-id="add-section-button"
          >
            <PlusCircle className="h-4 w-4 mr-2" />
            Add Section
          </Button>
        </div>
      </CardContent>
      
      <AddSectionDialog
        isOpen={isAddingSectionDialog}
        onClose={() => setIsAddingSectionDialog(false)}
        onAddSection={handleAddSection}
        isSubmitting={isSubmitting}
      />
      
      <EditSectionDialog
        isOpen={!!editingSection}
        onClose={() => setEditingSection(null)}
        onSectionUpdated={onSectionUpdated}
        isParentSubmitting={isSubmitting}
        section={editingSection}
      />
      
      <EditStepDialog
        isOpen={isEditingStep}
        onClose={() => setIsEditingStep(false)}
        onUpdateStep={handleUpdateStep}
        step={step}
        isSubmitting={isSubmitting}
      />
    </Card>
  );
};

export default StepItem;
